import React, { useState } from 'react';
import {
  Building2,
  AlertCircle,
  Mail,
  Lock,
  Eye,
  User,
  ArrowLeft,
  Users,
  CheckCircle,
} from 'lucide-react';

export default function SignUpPage({ setAuthPage, onSignup, onGoogleAuth, error, isLoading }) {
  const [name, setName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [joinMode, setJoinMode] = useState('create');
  const [teamCode, setTeamCode] = useState('');

  const getPasswordErrors = (pw) => {
    const errors = [];
    if (pw.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(pw)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pw)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pw)) errors.push('One number');
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');
    if (!name || !email || !password || !confirmPassword) {
      setLocalError('Please fill in all required fields');
      return;
    }
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    const pwErrors = getPasswordErrors(password);
    if (pwErrors.length > 0) {
      setLocalError('Password needs: ' + pwErrors.join(', '));
      return;
    }
    if (joinMode === 'join' && (teamCode.length !== 8 || !/^[A-Z0-9]+$/.test(teamCode))) {
      setLocalError('Please enter a valid 8-character alphanumeric team code');
      return;
    }
    onSignup(
      name,
      email,
      password,
      joinMode === 'create' ? teamName : '',
      joinMode === 'join' ? teamCode : ''
    );
  };

  const displayError = localError || error;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .auth-input { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: white; }
        .auth-input:focus { outline: none; border-color: #3b82f6; background: rgba(255,255,255,0.1); }
        .auth-input::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button
            onClick={() => setAuthPage('landing')}
            className="inline-flex items-center gap-3 mb-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Building2 size={24} className="text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-white tracking-tight">FlipperPro</h1>
            </div>
          </button>
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">Create an account</h2>
            <p className="text-blue-200 text-sm">Get started with FlipperPro</p>
          </div>

          {displayError && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 mb-4 flex items-center gap-2">
              <AlertCircle size={16} className="text-red-400" />
              <p className="text-red-200 text-sm">{displayError}</p>
            </div>
          )}

          <button
            type="button"
            onClick={() =>
              onGoogleAuth(
                joinMode,
                joinMode === 'create' ? teamName : '',
                joinMode === 'join' ? teamCode : ''
              )
            }
            disabled={isLoading}
            className="w-full py-3.5 rounded-xl text-sm font-semibold text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-white/10"></div>
            <span className="text-xs text-blue-300/60 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-white/10"></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">Full Name *</label>
              <div className="relative">
                <User
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300"
                />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="auth-input w-full pl-12 pr-4 py-3.5 rounded-xl text-sm"
                  placeholder="John Smith"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input w-full pl-12 pr-4 py-3.5 rounded-xl text-sm"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            {/* Team Mode Toggle */}
            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">Workspace Type</label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setJoinMode('create')}
                  className={`py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${joinMode === 'create' ? 'bg-blue-500 text-white' : 'bg-white/5 text-blue-200 border border-white/10 hover:bg-white/10'}`}
                >
                  Start My Own
                </button>
                <button
                  type="button"
                  onClick={() => setJoinMode('join')}
                  className={`py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${joinMode === 'join' ? 'bg-blue-500 text-white' : 'bg-white/5 text-blue-200 border border-white/10 hover:bg-white/10'}`}
                >
                  Join a Team
                </button>
              </div>

              {joinMode === 'create' ? (
                <div className="relative">
                  <Building2
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300"
                  />
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    className="auth-input w-full pl-12 pr-4 py-3.5 rounded-xl text-sm"
                    placeholder="My Flipping Company (optional)"
                  />
                </div>
              ) : (
                <div>
                  <div className="relative">
                    <Users
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300"
                    />
                    <input
                      type="text"
                      value={teamCode}
                      onChange={(e) => setTeamCode(e.target.value.toUpperCase())}
                      className="auth-input w-full pl-12 pr-4 py-3.5 rounded-xl text-sm font-mono tracking-widest text-center"
                      placeholder="XXXXXXXX"
                      maxLength={8}
                    />
                  </div>
                  <p className="text-xs text-blue-300/70 mt-2">
                    Enter the 8-character team code from your manager or team admin
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">Password *</label>
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-input w-full pl-12 pr-12 py-3.5 rounded-xl text-sm"
                  placeholder="********"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-300 hover:text-white"
                >
                  <Eye size={18} />
                </button>
              </div>
              {password.length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                  {[
                    { label: '8+ characters', met: password.length >= 8 },
                    { label: 'Uppercase letter', met: /[A-Z]/.test(password) },
                    { label: 'Lowercase letter', met: /[a-z]/.test(password) },
                    { label: 'Number', met: /[0-9]/.test(password) },
                  ].map(({ label, met }) => (
                    <p
                      key={label}
                      className={`text-xs flex items-center gap-1.5 ${met ? 'text-emerald-400' : 'text-blue-300/50'}`}
                    >
                      {met ? (
                        <CheckCircle size={12} />
                      ) : (
                        <span className="w-3 h-3 rounded-full border border-blue-300/30 inline-block flex-shrink-0" />
                      )}
                      {label}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">
                Confirm Password *
              </label>
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="auth-input w-full pl-12 pr-4 py-3.5 rounded-xl text-sm"
                  placeholder="********"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 transition-all shadow-lg shadow-blue-500/30 disabled:opacity-50"
            >
              {isLoading
                ? 'Creating account...'
                : joinMode === 'join'
                  ? 'Create Account & Join Team'
                  : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-blue-200 text-sm mt-6">
            Already have an account?{' '}
            <button
              onClick={() => setAuthPage('signin')}
              className="text-blue-400 hover:text-blue-300 font-semibold"
            >
              Sign in
            </button>
          </p>
        </div>

        <button
          onClick={() => setAuthPage('landing')}
          className="mt-6 mx-auto flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} /> Back to Home
        </button>
      </div>
    </div>
  );
}
